import React, { useState, useRef, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = ["All", "Basketball", "Other Sports", "Concerts"];

function useInView(ref, threshold = 0.1) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef);

  useEffect(() => {
    setLoading(true);
    const params = activeCategory !== "All" ? `?category=${encodeURIComponent(activeCategory)}` : "";
    axios.get(`${API}/portfolio${params}`)
      .then((res) => setItems(res.data))
      .catch((err) => console.error("Failed to load portfolio:", err))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const filtered = items;

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goNext = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev + 1) % filtered.length);
    }
  };
  const goPrev = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <>
      {/* Page Header */}
      <section ref={headerRef} className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            className={`transition-all duration-700 ${
              headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
              Portfolio
            </span>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-tight text-white mt-2 mb-4">
              The Work
            </h1>
            <p className="text-neutral-400 text-lg max-w-xl">
              A curated collection of my favorite shots from courtside to concert halls.
            </p>
          </div>

          {/* Category Filters */}
          <div
            className={`flex flex-wrap gap-3 mt-10 transition-all duration-700 delay-200 ${
              headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-amber-500 text-black"
                    : "bg-[#1a1a1a] text-neutral-400 hover:text-white hover:bg-[#252525]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item, index) => (
              <GalleryItem
                key={item.id}
                item={item}
                index={index}
                onClick={() => openLightbox(index)}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-neutral-500 text-lg">No photos in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors duration-200 z-10"
            aria-label="Close lightbox"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 sm:left-8 text-white/50 hover:text-white transition-colors duration-200 z-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 sm:right-8 text-white/50 hover:text-white transition-colors duration-200 z-10"
            aria-label="Next image"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div
            className="max-w-5xl max-h-[85vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filtered[lightboxIndex].image.replace("w=800", "w=1400")}
              alt={filtered[lightboxIndex].title}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <h3 className="font-bebas text-2xl text-white tracking-wide">
                {filtered[lightboxIndex].title}
              </h3>
              <p className="text-neutral-500 text-sm mt-1">
                {filtered[lightboxIndex].description}
              </p>
              <span className="text-amber-500 text-xs font-semibold uppercase tracking-wider mt-2 inline-block">
                {filtered[lightboxIndex].category}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function GalleryItem({ item, index, onClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, 0.05);

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      } ${
        index % 5 === 0 ? "sm:col-span-2 aspect-[16/9]" : "aspect-[4/3]"
      }`}
      style={{ transitionDelay: inView ? `${(index % 6) * 80}ms` : "0ms" }}
    >
      <img
        src={item.image}
        alt={item.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
        <span className="text-amber-500 text-xs font-semibold uppercase tracking-wider">
          {item.category}
        </span>
        <h3 className="font-bebas text-xl text-white mt-1 tracking-wide">
          {item.title}
        </h3>
        <p className="text-neutral-300 text-xs mt-1 line-clamp-1">{item.description}</p>
      </div>
    </div>
  );
}
